import { ref, watch, computed, onMounted } from "@vue/composition-api";
import store from "@/store";
import { formatCurrency } from "@core/utils/filter";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ability from "@/libs/acl/ability";

export default function useInvoicesList() {
  // Use toast
  const toast = useToast();

  const refInvoiceListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: "invoice_number", label: "編號", class: 'sticky-column', sortable: true },
    { key: "invoice_date", class: 'sticky-column', label: "發單日期", sortable: true },
    { key: "actions", label: "操作", class: 'sticky-column' },
    { key: "show_details2", label: "階段收費"},
    { key: "show_details", label: "項目摘要"},
    { key: "stamp", label: "公司印章" },
    { key: "customer.name_cn", label: "單據抬頭" },
    { key: "invoice_type", label: "類別" },
    { key: "project_name", label: "項目名稱", sortable: true },
    { key: "address", label: "施工地址", sortable: true },
    { key: "amount", label: "總價", sortable: true, formatter: formatCurrency },
    // { key: "deposit_amount", label: "第一次付款金額", sortable: true, formatter: formatCurrency },
    // { key: 'total_deposit_paid', label: "已付第一次付款", formatter: formatCurrency },
    { key: 'total_paid', label: "已付", formatter: formatCurrency },
    { key: 'total_unpaid', label: "未付", formatter: formatCurrency },
    { key: 'total_refund', label: "總退款額", formatter: formatCurrency },
    { key: 'total_bad_debt_amount', label: "總壞帳額", formatter: formatCurrency },
    // { key: "deposit_due_date", label: "第一次付款截止日期", sortable: true },
    // { key: "deposit_paid_date", label: "第一次付款支付期日", sortable: true },
    /* 
    { key: "name_en", label: "系統帳號名稱" }, */
    { key: "mobile", label: "聯絡電話" },
    { key: "email", label: "電郵地址"},
    { key: "quotation_id", label: "報價單編號", sortable: true },
    { key: "created_at", label: "創建日期", sortable: true },
  ];
  const perPage = ref(10);
  const totalRow = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("invoice_date");
  const isSortDirDesc = ref(true);

  const invoicesList = ref(null);

  const filter_payment_status = ref(null);
  const filter_start_date = ref(null);
  const filter_end_date = ref(null);
  const filter_min_amount = ref(null);
  const filter_max_amount = ref(null);
  const filter_invoice_type_id = ref(null);
  const filter_project_id = ref(null);
  const filter_customer_id = ref(null);
  const filter_product_id = ref(null);
  const filter_quotation_id = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refInvoiceListTable.value
      ? refInvoiceListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRow.value,
    };
  });

  const refetchData = () => {
    refInvoiceListTable.value.refresh();
  };

  watch(
    [
      currentPage, perPage, searchQuery, 
      filter_invoice_type_id, filter_project_id, filter_customer_id,
      filter_product_id, filter_quotation_id, filter_payment_status,
      filter_start_date, filter_end_date, filter_min_amount, filter_max_amount
    ],
    () => {
      refetchData();
    }
  );

  const fetchInvoices = (ctx, callback) => {

    store
      .dispatch("invoice/fetchInvoices", {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        invoice_type_id: filter_invoice_type_id.value,
        quotation_id: filter_quotation_id.value,
        project_id : filter_project_id.value,
        customer_id: filter_customer_id.value,
        product_id: filter_product_id.value,
        payment_status: filter_payment_status.value,
        start_date: filter_start_date.value,
        end_date: filter_end_date.value,
        min_amount: filter_min_amount.value,
        max_amount: filter_max_amount.value
      })
      .then((response) => {
        const { invoices, total } = response.data;
        callback(invoices);
        totalRow.value = total;
        invoicesList.value = invoices
      })
      .catch((err) => {
        console.log(err);
        toast({
          component: ToastificationContent,
          props: {
            title: "獲取貸款清單時出錯",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const invoiceTypeOptions = ref([]);
  const quotationOptions = ref([]);
  const customerOptions = ref([]);
  const productCategoriesOptions = ref([]);
  const productOptions = ref([]);
  const workerOptions = ref([]);
  const projectOptions = ref([])

  const refetchOption = () => {
    store
      .dispatch("app/fetchOptionList", {
        quotation_types: true,
        quotations: true,
        customers: true,
        products: true, 
        product_categories: true,
        projects: true,
      })
      .then((response) => {
        invoiceTypeOptions.value = response.data.quotation_types;
        quotationOptions.value = response.data.quotations;
        customerOptions.value = response.data.customers;
        productOptions.value = response.data.products;
        productCategoriesOptions.value = response.data.product_categories;
        workerOptions.value = response.data.workers;
        projectOptions.value = response.data.projects;

      });
  };

  onMounted(() => {
    refetchOption();
  });

  return {
    fetchInvoices,
    tableColumns,
    perPage,
    currentPage,
    totalRow,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    invoicesList,
    refInvoiceListTable,
    ability,
    refetchData,
    filter_invoice_type_id,
    filter_project_id,
    filter_customer_id,
    filter_product_id,
    filter_quotation_id,
    filter_payment_status,
    filter_start_date,
    filter_end_date,
    filter_min_amount,
    filter_max_amount,
    invoiceTypeOptions,
    quotationOptions,
    customerOptions,
    productOptions,
    workerOptions,
    projectOptions,
    productCategoriesOptions,
  };
}
